/* eslint-disable react-native/no-inline-styles */
/*
Author: Gilang Romadhanu Tartila
*/

import React, { useState, useEffect } from 'react';
import {
  View,
  StyleSheet,
  TouchableOpacity,
  Dimensions,
  ScrollView,
} from 'react-native';
import { useDispatch, useSelector } from 'react-redux';
import Button from '@components/form/Button';
import SociollaText from '@components/sociolla/SociollaText';
import Table from '@components/table/Table';
import ListStickyHeader from '@/screens/catalog/components/ListStickyHeader';
import { isWeb, isAndroid, isMobileWeb } from '@global/screens';
import Colors from '@global/colors';
import useApi from '@/hooks/useApi';
import { MsCatalog } from '@api/ms-catalog';
import { showPopup } from '@/store/modal';
import ModalFull from '@components/modal/ModalFull';
import * as Layout from '@components/screen/Layout';
import BrandSearch from '@components/form/search/BrandSearch';
import store from '@/store';
import { paramsReseted } from '@/store/params';

import PropTypes from 'prop-types';

import { exportCsvApi, isVn } from '@global/helpers';

const dimension = Dimensions.get('screen');

const propTypes = {
  navigation: PropTypes.object,
  activeTab: PropTypes.string,
  onChangeTab: PropTypes.func,
  mainBanner: PropTypes.oneOfType([PropTypes.node, PropTypes.bool]),
};

const defaultProps = {
  navigation: {},
  activeTab: 'categories',
  onChangeTab: () => {},
  mainBanner: false,
};

const tableStructure = {
  category_name: {
    head: 'Category Name',
    body: 'name',
  },
  total_products: {
    head: 'Total Product',
    body: 'total_products',
  },
};

const Categories = (props) => {
  const {
    activeTab = 'categories',
    mainBanner,
    onChangeTab = () => {},
  } = props;

  const dispatch = useDispatch();
  const paramsState = useSelector((state) => state.params);
  const showCatalogExport = useSelector(
    (state) => state.modal.showCatalogExport,
  );

  const [tableData, setTableData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [totalData, setTotalData] = useState(0);
  const [filterExport, setFilterExport] = useState({
    categories: {},
  });

  const getCategories = useApi(MsCatalog.getCategories);
  const getCategoriesCount = useApi(MsCatalog.getCategoriesCount);
  const getProductCount = useApi(MsCatalog.getProductCount);

  useEffect(() => {
    let mounted = true;
    if (mounted) {
      onReset();
      doSearch();
      dispatch(
        showPopup({
          name: 'showCatalogExport',
          value: false,
        }),
      );
    }

    return () => {
      dispatch(paramsReseted());
      mounted = false;
    };
  }, [props]);

  const onReset = () => {
    setTableData([]);
    setTotalData(0);
    dispatch(paramsReseted());
  };

  const getCategoryProductCount = async (categoryId) => {
    try {
      const response = await getProductCount.request({
        params: {
          filter: {
            'categories.id': categoryId,
            ...(isVn()
              ? { is_active_in_b2b_vn: true }
              : { is_active_in_b2b: true }),
          },
        },
      });

      return (
        response?.data?.data ||
        response?.data?.count ||
        response?.data?.total ||
        0
      );
    } catch (error) {
      console.log('error-get-product-count', error);
      return 0;
    }
  };

  const attachProductCountToCategories = async (categories = []) => {
    if (!categories.length) {
      return [];
    }

    const categoriesWithCount = await Promise.all(
      categories.map(async (item) => {
        const totalProducts = item?._id
          ? await getCategoryProductCount(item._id)
          : 0;

        return {
          ...item,
          total_products: totalProducts,
        };
      }),
    );

    return categoriesWithCount;
  };

  const doSearch = async (newSkip = 0, keyword = '') => {
    const isLoadMore = !isWeb && newSkip > 0;

    if (isLoadMore) {
      setLoadingMore(true);
    } else {
      setLoading(true);
    }

    try {
      const param = JSON.parse(JSON.stringify(store.getState().params));

      const params = {
        params: {
          filter: {
            ...(isVn()
              ? { is_active_in_b2b_vn: true }
              : { is_active_in_b2b: true }),
          },
          skip: typeof newSkip === 'number' ? newSkip : param.skip,
          limit: param.limit,
        },
      };

      if (keyword) {
        params.params.filter.name = { $regex: keyword, $options: 'i' };
      } else if (
        param?.filter?.name &&
        typeof param?.filter?.name === 'object'
      ) {
        params.params.filter.name = param.filter.name;
      }

      if (params.params.skip === 0) {
        const countResponse = await getCategoriesCount.request(params);
        const total =
          countResponse?.data?.data ||
          countResponse?.data?.count ||
          countResponse?.data?.total ||
          0;
        params.totalData = total;
      }

      const response = await getCategories.request(params);
      const result = Array.isArray(response?.data?.data)
        ? response.data.data
        : [];

      const mappedResult = result.map((item) => ({
        ...item,
        name: isVn() && item?.i18n?.vi?.name ? item.i18n.vi.name : item.name,
      }));

      const categoriesWithProductCount = await attachProductCountToCategories(
        mappedResult,
      );

      if (isLoadMore) {
        const newResult = tableData.concat(categoriesWithProductCount);
        setTableData(newResult);
      } else {
        setTableData(categoriesWithProductCount);
      }

      if (params.totalData !== undefined) {
        setTotalData(params.totalData);
      } else if (!isLoadMore && !result.length) {
        setTotalData(0);
      }

      if (isLoadMore) {
        setLoadingMore(false);
      } else {
        setLoading(false);
      }
    } catch (e) {
      if (isLoadMore) {
        setLoadingMore(false);
      } else {
        setLoading(false);
      }
      console.log(e);
    }
  };

  const doSearchByKeyword = async (value) => {
    setTableData([]);
    setTotalData(0);
    await doSearch(0, value);
  };

  const gotToDetail = (data) => {
    if (data?._id) {
      dispatch(paramsReseted());
      props.navigation.navigate('CatalogCategoriesScreen', {
        titleScreen: data.name,
        name: data.name,
        id: data._id,
      });
    }
  };

  const handleExportCSV = async () => {
    try {
      const payload = {};
      await exportCsvApi('categories-export', payload, {
        isDownload: false,
        message: 'Export in progress, the result will be sent to your email.',
      });
    } catch (error) {
      console.log('error-export', error);
    }
  };

  return (
    <>
      {isWeb ? (
        <Table
          name="catalog-categories"
          structures={tableStructure}
          data={tableData}
          loading={loading}
          hasCallback
          hasNoHide
          noRowColor
          rowBottomBorder
          showFilter={false}
          totalData={totalData}
          skip={paramsState.skip}
          limit={paramsState.limit}
          searchKey="name"
          searchPlaceholder="Search by name..."
          onClickRow={({ data }) => gotToDetail(data)}
          onSearch={(value) => doSearchByKeyword(value)}
          onLoadMore={(newSkip) => doSearch(newSkip)}
          moreInlineFilter={
            <>
              <Button
                type="primary"
                rounded={true}
                buttonStyle={{ width: 90 }}
                title={
                  <SociollaText
                    type="medium"
                    style={{ fontSize: 14, color: Colors.white }}>
                    Export
                  </SociollaText>
                }
                onPress={() => handleExportCSV()}
                style={{ marginRight: 8 }}
              />
            </>
          }
        />
      ) : (
        <ListStickyHeader
          name="catalog-categories"
          searchKey="name"
          placeholder="Search by name"
          loading={loading}
          onSearch={(value) => doSearchByKeyword(value)}
          data={tableData}
          activeTab={activeTab}
          onChangeTab={(val) => onChangeTab(val)}
          mainBanner={mainBanner}
          withLoadMore
          withFetch
          loadingPagination={loadingMore}
          totalData={totalData}
          skip={paramsState.skip}
          limit={paramsState.limit}
          onLoadMore={(newSkip) => doSearch(newSkip)}
          renderItem={({ data, index }) => (
            <TouchableOpacity
              activeOpacity={0.7}
              style={styles.categoryItem}
              onPress={() => gotToDetail(data)}
              key={index}>
              <View style={styles.categoryContent}>
                <SociollaText type="medium" style={styles.categoryTitle}>
                  {data?.name || '-'}
                </SociollaText>
                <SociollaText style={styles.categoryCount}>
                  {data?.total_products || 0}
                  {' products'}
                </SociollaText>
              </View>
            </TouchableOpacity>
          )}
        />
      )}

      <ModalFull
        title="Export Catalog"
        noTranslate={true}
        show={showCatalogExport}
        onClose={() =>
          dispatch(
            showPopup({
              name: 'showCatalogExport',
              value: false,
            }),
          )
        }>
        <View
          style={{
            height: isWeb
              ? 'calc(100vh - 150px)'
              : dimension.height - (isAndroid ? 140 : 110),
          }}>
          <ScrollView contentContainerStyle={{ width: '100%' }}>
            <Layout.FormWrapper style={{ padding: 15, height: '100%' }}>
              <Layout.FormGroup lastItem>
                <SociollaText style={{ fontSize: 13, marginBottom: 10 }}>
                  Brands
                </SociollaText>
                <BrandSearch
                  hideLabel={true}
                  isRequired
                  value={filterExport.brand}
                  allBrand={true}
                  onChange={(value) => {
                    setFilterExport({ ...filterExport, brand: value });
                  }}
                />
              </Layout.FormGroup>
            </Layout.FormWrapper>
          </ScrollView>

          <View
            style={{
              width: '100%',
              flexDirection: 'row',
              alignItems: 'flex-end',
              justifyContent: isWeb ? 'end' : 'space-between',
              paddingVertical: isWeb ? 20 : 15,
              paddingHorizontal: isWeb ? 20 : 15,
              backgroundColor: Colors.white,
              ...(!isWeb
                ? {
                    position: isMobileWeb ? 'fixed' : 'absolute',
                    bottom: 0,
                    right: 0,
                    left: 0,
                  }
                : {}),
            }}>
            <Button
              rounded={true}
              type="primary"
              buttonStyle={{
                width: isWeb
                  ? 100
                  : dimension.width / 2 - (isMobileWeb ? 20 : 25),
                backgroundColor: Colors.white,
                marginRight: 10,
              }}
              title={
                <SociollaText type="medium" style={{ fontSize: 14 }}>
                  Cancel
                </SociollaText>
              }
              onPress={() =>
                dispatch(
                  showPopup({
                    name: 'showCatalogExport',
                    value: false,
                  }),
                )
              }
            />
            <Button
              rounded={true}
              type="primary"
              buttonStyle={{
                width: isWeb
                  ? 100
                  : dimension.width / 2 - (isMobileWeb ? 20 : 25),
              }}
              disabledStyle={{ width: 90 }}
              title={
                <SociollaText
                  type="medium"
                  style={{ fontSize: 14, color: Colors.white }}>
                  Export
                </SociollaText>
              }
              onPress={() => handleExportCSV()}
            />
          </View>
        </View>
      </ModalFull>
    </>
  );
};

Categories.propTypes = propTypes;
Categories.defaultProps = defaultProps;

const styles = StyleSheet.create({
  categoryItem: {
    width: '100%',
    backgroundColor: Colors.white,
    borderBottomWidth: 1,
    borderBottomColor: Colors.grey0,
  },
  categoryContent: {
    paddingVertical: 16,
    paddingHorizontal: 16,
  },
  categoryTitle: {
    fontSize: 15,
    color: Colors.black,
  },
  categoryCount: {
    fontSize: 13,
    marginTop: 6,
    color: Colors.grey2,
  },
});

export default React.memo(Categories, (prev, next) => prev == next);
